import { useState, useEffect, useCallback } from "react";
import { getToken } from "utils/auth";
import { BASE_URL, resolveAssetUrl } from "utils/api";
import { useNavigate } from "react-router-dom";
const MOBILE_BASE = `${BASE_URL}/mobile`;

// ─── constants ────────────────────────────────────────────────────────────────
const FONT   = "'DM Sans',sans-serif";
const LIMIT  = 15;

const INPUT = {
  width: "100%", padding: "9px 13px", borderRadius: 8,
  border: "1.5px solid #e5e7eb", fontSize: 14, color: "#111827",
  background: "#fafafa", outline: "none", boxSizing: "border-box",
  fontFamily: FONT,
};
const LABEL = {
  fontSize: 11, fontWeight: 700, color: "#6b7280",
  marginBottom: 5, display: "block", letterSpacing: .5,
  textTransform: "uppercase",
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const statusColor = (s) => ({
  SENT: { bg: "#dcfce7", text: "#166534" },
  PENDING: { bg: "#fff7ed", text: "#9a3412" },
  SCHEDULED: { bg: "#eff6ff", text: "#1e40af" },
  FAILED: { bg: "#fee2e2", text: "#991b1b" },
  READ: { bg: "#f3e8ff", text: "#6b21a8" },
}[s] || { bg: "#f3f4f6", text: "#374151" });

const fmt = (iso) => iso
  ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 9999,
      background: type === "success" ? "#166534" : "#991b1b",
      color: "#fff", padding: "12px 22px", borderRadius: 10,
      fontFamily: FONT, fontSize: 14, fontWeight: 500,
      boxShadow: "0 8px 32px rgba(0,0,0,.22)",
      animation: "slideIn .3s ease",
    }}>{msg}</div>
  );
}

// ResendModal
function ResendModal({ notif, onClose, onDone, showToast }) {
  const [mode, setMode]         = useState("now");   // "now" | "schedule"
  const [schedAt, setSchedAt]   = useState("");
  const [busy, setBusy]         = useState(false);
  const token = getToken();

  // min datetime = now + 1 min (browser format yyyy-MM-ddTHH:mm)
  const minDt = new Date(Date.now() + 60_000).toISOString().slice(0, 16);

  const submit = async () => {
    if (mode === "schedule" && !schedAt) {
      showToast("Pick a date/time first", "error");
      return;
    }
    setBusy(true);
    try {
      const body = {
        title_en:       notif.title_en,
        title_ar:       notif.title_ar       || undefined,
        description_en: notif.description_en || undefined,
        description_ar: notif.description_ar || undefined,
        image_en:       notif.image_en       || undefined,
        image_ar:       notif.image_ar       || undefined,
        customer_id:    notif.customer_id    || undefined,
        scheduled_at:   mode === "schedule" ? new Date(schedAt).toISOString() : undefined,
      };

      const res  = await fetch(`${MOBILE_BASE}/notification/send`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify(body),
      });
      const data = await res.json();

      if (data.status) {
        showToast(mode === "schedule" ? "Notification scheduled!" : "Notification sent!");
        onDone();
        onClose();
      } else {
        showToast(data.message || "Failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
          zIndex: 1000, animation: "fadeIn .2s ease",
        }}
      />

      {/* modal */}
      <div style={{
        position: "fixed", top: "50%", left: "50%", zIndex: 1001,
        transform: "translate(-50%,-50%)",
        background: "#fff", borderRadius: 16, padding: 28,
        width: "min(460px,92vw)", boxShadow: "0 24px 64px rgba(0,0,0,.18)",
        animation: "popIn .25s ease",
        fontFamily: FONT,
      }}>

        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#14532d" }}>Resend Notification</p>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: "#6b7280" }}>
              Send immediately or schedule for later
            </p>
          </div>
          <button onClick={onClose} style={{
            background: "#f3f4f6", border: "none", borderRadius: 8,
            width: 30, height: 30, cursor: "pointer", fontSize: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>
        </div>

        {/* preview pill */}
        <div style={{
          padding: "12px 14px", borderRadius: 10,
          background: "#f0fdf4", border: "1px solid #bbf7d0",
          display: "flex", gap: 10, alignItems: "center", marginBottom: 20,
        }}>
          {notif.image_en
            ? <img src={resolveAssetUrl(notif.image_en)} alt=""
                style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                onError={(e) => (e.target.style.display = "none")} />
            : <div style={{
                width: 36, height: 36, borderRadius: 8, background: "#16a34a",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0,
              }}>🔔</div>
          }
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#14532d",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {notif.title_en}
            </p>
            {notif.description_en && (
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {notif.description_en}
              </p>
            )}
          </div>
        </div>

        {/* mode toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {[
            { key: "now",      label: "⚡ Send Now" },
            { key: "schedule", label: "🗓 Schedule" },
          ].map((o) => (
            <button
              key={o.key}
              onClick={() => setMode(o.key)}
              style={{
                flex: 1, padding: "9px 0", borderRadius: 8, cursor: "pointer",
                fontSize: 13, fontWeight: 600, fontFamily: FONT, transition: "all .15s",
                border:    `1.5px solid ${mode === o.key ? "#16a34a" : "#e5e7eb"}`,
                background: mode === o.key ? "#dcfce7" : "#fff",
                color:      mode === o.key ? "#14532d" : "#6b7280",
              }}
            >{o.label}</button>
          ))}
        </div>

        {/* datetime picker (schedule mode) */}
        {mode === "schedule" && (
          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Schedule Date &amp; Time</label>
            <input
              type="datetime-local"
              min={minDt}
              value={schedAt}
              onChange={(e) => setSchedAt(e.target.value)}
              style={{ ...INPUT, colorScheme: "light" }}
            />
            {schedAt && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
                Will send: {fmt(new Date(schedAt).toISOString())}
              </p>
            )}
          </div>
        )}

        {/* target info */}
        {notif.customer_id && (
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
            📌 Target: Customer #{notif.customer_id}
          </p>
        )}
        {!notif.customer_id && (
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
            📢 Target: All Customers (Broadcast)
          </p>
        )}

        {/* actions */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "9px 20px", borderRadius: 8, border: "1.5px solid #e5e7eb",
            background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600,
            cursor: "pointer", fontFamily: FONT,
          }}>Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            style={{
              padding: "9px 24px", borderRadius: 8, border: "none",
              background: busy ? "#86efac" : "#16a34a", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer",
              fontFamily: FONT, transition: "background .2s",
            }}
          >{busy ? "Sending..." : mode === "schedule" ? "Schedule" : "Send Now"}</button>
        </div>
      </div>
    </>
  );
}

export default function NotificationHistory() {
  const [notifs, setNotifs]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [toast, setToast]           = useState(null);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [resendTarget, setResendTarget] = useState(null); // notif being resent

  const token     = getToken();
  const showToast = (msg, type = "success") => setToast({ msg, type });
   const navigate = useNavigate();


  const fetchHistory = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res  = await fetch(`${MOBILE_BASE}/notification/history?page=${p}&limit=${LIMIT}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) {
        setNotifs(data.data || []);
        setTotalPages(data.pagination?.total_pages || 1);
        setPage(p);
      } else {
        showToast(data.message || "Failed to load", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchHistory(1); }, [fetchHistory]);

  // ── mark read
  const markRead = async (id) => {
    try {
      await fetch(`${MOBILE_BASE}/notification/${id}/read`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}` },
      });
      setNotifs((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true, status: "READ" } : n));
      showToast("Marked as read");
    } catch {
      showToast("Failed to mark as read", "error");
    }
  };

  // ── pagination helper ─────────────────────────────────────────────────────
  const pageBtns = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, 2, page - 1, page, page + 1, totalPages - 1, totalPages]);
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  };

  return (
    <div style={{ fontFamily: FONT, background: "#f0faf4", minHeight: "100vh", padding: "28px 32px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes slideIn { from{transform:translateY(16px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes popIn   { from{transform:translate(-50%,-48%);opacity:0} to{transform:translate(-50%,-50%);opacity:1} }
        input[type=datetime-local]:focus { border-color:#16a34a!important; box-shadow:0 0 0 3px rgba(22,163,74,.12); }
        .nrow { transition: background .15s; }
        .nrow:hover { background: #f9fafb !important; }
        .resend-btn { opacity:0; transition:opacity .15s; }
        .nrow:hover .resend-btn { opacity:1 !important; }
      `}</style>

    {/* Header */}
<div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 28,
  }}
>
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        background: "#16a34a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 22,
      }}
    >
      📋
    </div>

    <div>
      <p
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: "#14532d",
          margin: 0,
        }}
      >
        Notification History
      </p>

      <p
        style={{
          fontSize: 13,
          color: "#6b7280",
          margin: 0,
        }}
      >
        All sent & scheduled push notifications
      </p>
    </div>
  </div>

  <button
    onClick={() => navigate("/notification/send")}
    style={{
      border: "none",
      borderRadius: 10,
      background: "#16a34a",
      color: "#fff",
      padding: "10px 18px",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
      fontFamily: FONT,
    }}
  >
    + Send Notification
  </button>
</div>

      {/* Card */}
      <div style={{
        background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
        boxShadow: "0 1px 8px rgba(0,0,0,.05)", padding: 28,
      }}>

        {/* toolbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <p style={{ fontSize: 15, fontWeight: 700, color: "#14532d", margin: 0 }}>
            All Notifications
            {notifs.length > 0 && (
              <span style={{
                marginLeft: 8, fontSize: 11, fontWeight: 600,
                background: "#dcfce7", color: "#166534",
                padding: "2px 8px", borderRadius: 99,
              }}>{notifs.length} shown</span>
            )}
          </p>
          <button
            onClick={() => fetchHistory(page)}
            style={{
              background: "#f0fdf4", border: "1px solid #bbf7d0",
              borderRadius: 8, padding: "6px 14px", cursor: "pointer",
              fontSize: 12, fontWeight: 600, color: "#16a34a", fontFamily: FONT,
            }}
          >↻ Refresh</button>
        </div>

        {/* list */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
            <p style={{ fontFamily: FONT, margin: 0 }}>Loading...</p>
          </div>
        ) : notifs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>🔔</div>
            <p style={{ fontFamily: FONT, fontSize: 15, margin: 0 }}>No notifications yet</p>
          </div>
        ) : (
          <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #f3f4f6" }}>
            {notifs.map((n, idx) => {
              const sc = statusColor(n.status);
              return (
                <div
                  key={n.id}
                  className="nrow"
                  style={{
                    padding: "16px 20px",
                    borderBottom: idx < notifs.length - 1 ? "1px solid #f3f4f6" : "none",
                    background: !n.is_read ? "#f0fdf4" : "#fff",
                    display: "flex", gap: 14, alignItems: "flex-start",
                  }}
                >
                  {/* unread dot */}
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%", marginTop: 7, flexShrink: 0,
                    background: !n.is_read ? "#16a34a" : "#e5e7eb",
                  }} />

                  {/* image thumbnail */}
                  {n.image_en && (
                    <img
                      src={resolveAssetUrl(n.image_en)} alt=""
                      style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  )}

                  {/* body */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#111827", flex: 1 }}>
                        {n.title_en}
                      </p>
                      {/* status badge */}
                      <span style={{
                        background: sc.bg, color: sc.text,
                        padding: "2px 9px", borderRadius: 99,
                        fontSize: 11, fontWeight: 600, letterSpacing: .4, flexShrink: 0,
                      }}>{n.status}</span>
                    </div>

                    {n.title_ar && (
                      <p style={{ margin: "2px 0 0", fontSize: 13, color: "#6b7280", direction: "rtl" }}>
                        {n.title_ar}
                      </p>
                    )}
                    {n.description_en && (
                      <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.description_en}
                      </p>
                    )}
                    {n.description_ar && (
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9ca3af", direction: "rtl",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.description_ar}
                      </p>
                    )}

                    {/* AR image thumbnail (smaller, inline) */}
                    {n.image_ar && (
                      <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700 }}>AR</span>
                        <img
                          src={resolveAssetUrl(n.image_ar)} alt="AR"
                          style={{ height: 32, borderRadius: 5, border: "1px solid #e5e7eb", objectFit: "cover" }}
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </div>
                    )}

                    {/* meta row */}
                    <div style={{
                      fontSize: 11, color: "#9ca3af", marginTop: 8,
                      display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                    }}>
                      {n.customer_id
                        ? <span style={{ background: "#eff6ff", color: "#1e40af", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>Customer</span>
                        : <span style={{ background: "#f3f4f6", color: "#374151", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>Broadcast</span>
                      }

                      {/* scheduled indicator */}
                      {n.scheduled_at && (
                        <span style={{ background: "#eff6ff", color: "#1e40af", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>
                          🗓 {fmt(n.scheduled_at)}
                        </span>
                      )}

                      <span>{fmt(n.createdAt)}</span>

                      {/* mark read */}
                      {!n.is_read && (
                        <button
                          onClick={() => markRead(n.id)}
                          style={{
                            fontSize: 11, color: "#16a34a", background: "none",
                            border: "none", cursor: "pointer", fontWeight: 600,
                            padding: 0, fontFamily: FONT,
                          }}
                        >✓ Mark read</button>
                      )}

                      {/* RESEND button — appears on row hover */}
                      <button
                        className="resend-btn"
                        onClick={() => setResendTarget(n)}
                        style={{
                          marginLeft: "auto", fontSize: 11, fontWeight: 700,
                          background: "#f0fdf4", border: "1px solid #bbf7d0",
                          color: "#16a34a", padding: "3px 10px", borderRadius: 6,
                          cursor: "pointer", fontFamily: FONT,
                        }}
                      >↩ Resend</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, paddingTop: 20, flexWrap: "wrap" }}>
            <button
              onClick={() => fetchHistory(page - 1)} disabled={page === 1}
              style={{
                padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e5e7eb",
                background: "#fff", color: page === 1 ? "#d1d5db" : "#374151",
                cursor: page === 1 ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 600, fontFamily: FONT,
              }}
            >← Prev</button>

            {pageBtns().reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1)
                acc.push(<span key={`ellipsis-${i}`} style={{ padding: "6px 4px", color: "#9ca3af" }}>…</span>);
              acc.push(
                <button key={p} onClick={() => fetchHistory(p)} style={{
                  width: 32, height: 32, borderRadius: 8,
                  border: `1.5px solid ${p === page ? "#16a34a" : "#e5e7eb"}`,
                  background: p === page ? "#16a34a" : "#fff",
                  color: p === page ? "#fff" : "#6b7280",
                  cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: FONT,
                }}>{p}</button>
              );
              return acc;
            }, [])}

            <button
              onClick={() => fetchHistory(page + 1)} disabled={page === totalPages}
              style={{
                padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e5e7eb",
                background: "#fff", color: page === totalPages ? "#d1d5db" : "#374151",
                cursor: page === totalPages ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 600, fontFamily: FONT,
              }}
            >Next →</button>
          </div>
        )}
      </div>

      {/* Resend modal */}
      {resendTarget && (
        <ResendModal
          notif={resendTarget}
          onClose={() => setResendTarget(null)}
          onDone={() => fetchHistory(page)}
          showToast={showToast}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}